import type { DirecaoMensagem, TipoMensagem } from './enums'

/**
 * O que dá para fazer com uma mensagem da conversa: responder, copiar, editar
 * e apagar.
 *
 * Mora no pacote comum porque a tela e o servidor precisam responder igual. A
 * tela esconde ou apaga a opção; o servidor recusa o pedido que chegar mesmo
 * assim, com a mesma frase. Uma regra escrita duas vezes vira duas regras no
 * dia em que alguém mexer numa só.
 *
 * As decisões (ADR 0026):
 * - responder e copiar valem para as nossas mensagens e para as do cliente;
 *   editar e apagar, só para as nossas, porque a do cliente é dele;
 * - só quem enviou edita ou apaga, nem o administrador mexe na mensagem de
 *   outra pessoa;
 * - os prazos são os do WhatsApp, que recusa depois deles.
 */

/** O WhatsApp aceita editar por 15 minutos depois do envio. */
export const MINUTOS_PARA_EDITAR = 15

/**
 * O WhatsApp aceita "apagar para todos" por cerca de dois dias e meio. O CRM
 * para em dois: perto do limite, o pedido pode chegar ao WhatsApp segundos
 * depois do prazo, e a tela diria "apagada" para uma mensagem que o cliente
 * continua vendo.
 */
export const HORAS_PARA_APAGAR = 48

export type AcaoDaMensagem = 'responder' | 'copiar' | 'editar' | 'apagar'

/** O pedaço da mensagem que as regras olham. */
export interface MensagemParaAcao {
  direcao: DirecaoMensagem
  tipo: TipoMensagem
  conteudo: string | null
  autorId: string | null
  enviadaEm: string | Date
  /** Existe no WhatsApp: tem identificador e não falhou no envio. */
  noWhatsapp: boolean
  apagadaEm: string | Date | null
}

/**
 * `disponivel`: a opção faz sentido para esta mensagem e esta pessoa. Quando
 * não faz, some da tela.
 * `bloqueio`: faz sentido, mas agora não dá, e a frase diz por quê. A opção
 * aparece apagada, para a pessoa saber que existe e por que não pode.
 */
export interface AvaliacaoDaAcao {
  disponivel: boolean
  bloqueio: string | null
}

const INDISPONIVEL: AvaliacaoDaAcao = { disponivel: false, bloqueio: null }
const LIBERADA: AvaliacaoDaAcao = { disponivel: true, bloqueio: null }

export function avaliarAcao(
  acao: AcaoDaMensagem,
  mensagem: MensagemParaAcao,
  usuarioId: string,
  agora: Date = new Date(),
  provedor: 'evolution' | 'oficial' = 'evolution',
): AvaliacaoDaAcao {
  const avaliacao = avaliarNaEvolution(acao, mensagem, usuarioId, agora)
  /*
    A API oficial da Meta não edita nem apaga mensagem enviada (ADR 0033).
    Aparece travado, com o motivo, em vez de sumir do menu: quem se acostumou
    com a opção no número antigo entende por que ela não está mais ali.
  */
  if (provedor === 'oficial' && (acao === 'editar' || acao === 'apagar') && avaliacao.disponivel) {
    return {
      disponivel: true,
      bloqueio:
        acao === 'editar'
          ? 'A API oficial do WhatsApp não permite editar mensagem enviada.'
          : 'A API oficial do WhatsApp não permite apagar mensagem enviada.',
    }
  }
  return avaliacao
}

function avaliarNaEvolution(
  acao: AcaoDaMensagem,
  mensagem: MensagemParaAcao,
  usuarioId: string,
  agora: Date,
): AvaliacaoDaAcao {
  // Aviso do sistema e mensagem já apagada não têm o que oferecer.
  if (mensagem.tipo === 'sistema' || mensagem.apagadaEm) return INDISPONIVEL

  const temTexto = Boolean(mensagem.conteudo?.trim())
  if (acao === 'copiar') return temTexto ? LIBERADA : INDISPONIVEL

  // Comentário da equipe não existe no WhatsApp: não se cita nem se apaga lá.
  if (mensagem.tipo === 'nota_interna' || !mensagem.noWhatsapp) return INDISPONIVEL

  if (acao === 'responder') return LIBERADA

  const minha = mensagem.direcao === 'saida' && mensagem.autorId === usuarioId
  if (!minha) return INDISPONIVEL

  const minutos = (agora.getTime() - new Date(mensagem.enviadaEm).getTime()) / 60_000

  if (acao === 'editar') {
    // O WhatsApp edita texto; legenda de foto e arquivo ficam como foram.
    if (mensagem.tipo !== 'texto' || !temTexto) return INDISPONIVEL
    return minutos > MINUTOS_PARA_EDITAR
      ? {
          disponivel: true,
          bloqueio: `O WhatsApp só deixa editar até ${MINUTOS_PARA_EDITAR} minutos depois do envio.`,
        }
      : LIBERADA
  }

  return minutos > HORAS_PARA_APAGAR * 60
    ? {
        disponivel: true,
        bloqueio: 'O WhatsApp só deixa apagar para todos até 2 dias depois do envio.',
      }
    : LIBERADA
}

/**
 * O trecho da mensagem citada, como aparece em cima da resposta.
 *
 * Sem a assinatura do vendedor: ela ocuparia a linha inteira do trecho e
 * esconderia o que foi dito. Mensagem sem texto vira o nome do tipo, como o
 * WhatsApp faz ("Foto", "Áudio").
 */
export function trechoParaCitar(conteudo: string | null, tipo: TipoMensagem): string {
  const texto = (conteudo ?? '').replace(/^\*[^*\n]{1,40}\*:?\s*\n+/, '').trim()
  if (texto) return texto.length > 160 ? `${texto.slice(0, 157)}...` : texto

  const rotulos: Partial<Record<TipoMensagem, string>> = {
    imagem: 'Foto',
    video: 'Vídeo',
    audio: 'Áudio',
    documento: 'Documento',
  }
  return rotulos[tipo] ?? 'Mensagem'
}
